"use client";

import { useState, Suspense } from "react";
import { motion } from "framer-motion";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Lock, ArrowLeft, CheckCircle } from "lucide-react";
import { cn } from "@/lib/utils";

function ResetForm() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) { setError("Token tidak valid"); return; }
    if (password.length < 6) { setError("Password minimal 6 karakter"); return; }
    if (password !== confirm) { setError("Password tidak cocok"); return; }
    setError("");
    setIsLoading(true);

    try {
      const API_BASE = (import.meta.env.VITE_API_URL || "") + "/api";
      const res = await fetch(`${API_BASE}/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, newPassword: password }),
      });
      const data = await res.json();
      if (res.ok) {
        setDone(true);
        setTimeout(() => navigate("/login"), 3000);
      } else {
        setError(data.error || "Gagal reset password");
      }
    } catch {
      setError("Gagal terhubung ke server");
    }
    setIsLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-light-bg dark:bg-dark-bg px-4 pt-20 pb-24">
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="w-full max-w-md">
        <Link to="/login" className="inline-flex items-center gap-2 text-sm text-light-text-secondary dark:text-dark-text-secondary hover:text-gold mb-8 transition-colors">
          <ArrowLeft className="w-4 h-4" /> Kembali ke Login
        </Link>

        <div className={cn("rounded-[28px] p-8", "bg-light-card dark:bg-dark-card", "border border-dark-text-secondary/5 dark:border-white/5", "shadow-sm")}>
          {done ? (
            <div className="text-center py-4">
              <CheckCircle className="w-16 h-16 text-emerald-400 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-light-text-primary dark:text-dark-text-primary mb-2">Password Berhasil Direset!</h2>
              <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary">Silakan login dengan password baru Anda.</p>
            </div>
          ) : !token ? (
            <div className="text-center py-4">
              <h2 className="text-xl font-bold text-red-400 mb-2">Link Tidak Valid</h2>
              <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary">Link reset password tidak valid atau sudah kadaluarsa.</p>
              <Link to="/forgot-password" className="inline-block mt-4 text-gold hover:text-gold-light font-medium">Minta link baru</Link>
            </div>
          ) : (
            <>
              <h2 className="text-xl font-bold text-light-text-primary dark:text-dark-text-primary mb-2">Reset Password</h2>
              <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary mb-6">Masukkan password baru Anda.</p>

              {error && <div className="mb-4 p-3 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 text-sm">{error}</div>}

              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-dark-text-secondary/50" />
                  <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password baru (min 6 karakter)"
                    className={cn("w-full pl-11 pr-4 py-3 rounded-2xl", "bg-light-bg dark:bg-dark-bg", "border border-dark-text-secondary/10 dark:border-white/10", "text-light-text-primary dark:text-dark-text-primary", "focus:outline-none focus:border-gold/40")} />
                </div>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-dark-text-secondary/50" />
                  <input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Konfirmasi password baru"
                    className={cn("w-full pl-11 pr-4 py-3 rounded-2xl", "bg-light-bg dark:bg-dark-bg", "border border-dark-text-secondary/10 dark:border-white/10", "text-light-text-primary dark:text-dark-text-primary", "focus:outline-none focus:border-gold/40")} />
                </div>
                <button type="submit" disabled={isLoading || !password || !confirm}
                  className={cn("w-full py-3.5 rounded-2xl font-semibold", "transition-all duration-300", password && confirm ? "bg-gold text-black hover:bg-gold-light" : "bg-dark-text-secondary/10 text-dark-text-secondary/40 cursor-not-allowed")}>
                  {isLoading ? "Menyimpan..." : "Simpan Password Baru"}
                </button>
              </form>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
}

export default function ResetPasswordPage() {
  return (
    <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" /></div>}>
      <ResetForm />
    </Suspense>
  );
}
